// De drie tabbladen van een kolom op het bord, elk met één getal. Actief en Geparkeerd: de som van de uren die nog te
// presteren zijn op de taken in dat tabblad (schatting min gelogd uit Clockify, hetzelfde getal als op de kaart).
// Afsluiten: het aantal taken, want daar is het werk gedaan (Rens, 28/10). Geen weekberekening, geen capaciteit, geen
// kleur (ontwerp Vrij en geparkeerd). Een taak zonder schatting of zonder Clockify-taak telt nul uur; de tip bij het
// getal zegt hoeveel dat er zijn. Een Clockify-taak die bij meerdere taken in dezelfde kolom hoort, telt één keer.
// Zonder uren (Clockify niet klaar, of geen recht) alleen de namen en het aantal bij Afsluiten. Het actieve tabblad is
// een kader dat opent in de lijst eronder (app.css, .p-ctabs).
import type { TaskHours } from "../lib/api-clockify";
import type { Task } from "../lib/types";

export type ColumnView = "actief" | "geparkeerd" | "afsluiten";

export interface TabSum {
  n: number;
  /** som van de rest over de taken met een schatting */
  hours: number;
  /** taken zonder schatting, zonder Clockify-taak of met een verdwenen Clockify-taak: tellen nul uur */
  noEst: number;
  withEst: number;
}

/** De som voor één tabblad; een gedeelde Clockify-taak telt één keer. */
export function tabSum(tasks: Task[], hours: Record<string, TaskHours>): TabSum {
  const seen = new Set<string>();
  let sum = 0, withEst = 0, noEst = 0;
  for (const t of tasks) {
    const h = hours[t.id];
    const cts = !h || h.none || h.missing ? []
      : h.tasks ? h.tasks.filter((c) => !c.missing && c.estimate != null)
      : h.estimate != null ? [{ clockifyTaskId: h.clockifyTaskId, estimate: h.estimate, logged: h.logged, rest: h.rest }] : [];
    if (!cts.length) { noEst++; continue; }
    withEst++;
    for (const c of cts) {
      if (seen.has(c.clockifyTaskId)) continue;
      seen.add(c.clockifyTaskId);
      sum += c.rest ?? Math.max(0, (c.estimate || 0) - (c.logged || 0));
    }
  }
  return { n: tasks.length, hours: sum, noEst, withEst };
}

interface Props {
  view: ColumnView;
  onView: (v: ColumnView) => void;
  /** "van Lennert" of "zonder eigenaar", voor de tip */
  who: string;
  act: Task[];
  parked: Task[];
  closing: Task[];
  /** uren per Planner-taak uit Clockify; null = geen uren tonen */
  hours: Record<string, TaskHours> | null;
}

export function ColumnTabs({ view, onView, who, act, parked, closing, hours }: Props) {
  const tab = (key: ColumnView, label: string, tasks: Task[], what: string) => {
    const s = hours ? tabSum(tasks, hours) : null;
    const title = !s ? tasks.length + " " + what + " " + who
      : "Nog te presteren op de " + s.n + " " + what + " " + who + ": schatting min gelogd in Clockify"
        + (s.noEst ? "; " + s.noEst + " zonder schatting " + (s.noEst === 1 ? "telt" : "tellen") + " niet mee" : "") + ".";
    return (
      <button type="button" role="tab" className="p-ctab" aria-selected={view === key} title={title} onClick={() => onView(key)}>
        <span className="k">{label}</span>
        {s && (!s.n ? <span className="v t-mute">–</span> : s.withEst ? <span className="v">{Math.round(s.hours)} u</span> : <span className="v t-mute">? u</span>)}
      </button>
    );
  };
  // Afsluiten: het aantal taken, ook zonder Clockify
  const n = closing.length;
  const closeTitle = "Nog open met label Prod of Test validated " + who + ": " + (n === 1 ? "1 taak" : n + " taken") + ".";
  return (
    <div className="p-ctabs" role="tablist" aria-label="Lijsten van de kolom">
      {tab("actief", "Actief", act, "actieve taken")}
      {tab("geparkeerd", "Geparkeerd", parked, "geparkeerde taken")}
      <button type="button" role="tab" className="p-ctab" aria-selected={view === "afsluiten"} title={closeTitle} onClick={() => onView("afsluiten")}>
        <span className="k">Afsluiten</span>
        {n ? <span className="v">{n}</span> : <span className="v t-mute">–</span>}
      </button>
    </div>
  );
}
